import {
  ExternalDataSourceType,
  ExternalTransaction,
  ExternalTransactionStatus,
} from '@helios/common-api'
import {
  BulkCashEntryTransaction,
  BulkDirectTransferTransaction,
  BulkFXForwardTransaction,
  BulkFXSpotTransaction,
  BulkLoadTransaction,
  BulkTradeEntryTransaction,
  BulkTransaction,
  BulkWireTransferTransaction,
} from '../model/bulk-transaction/bulk-transaction'
import { Currency } from '../model/currency.model'
import { SecurityMasterData } from '../model/security.model'
import { CashEntryTransaction } from '../model/single-transaction/cash-entry/cash-entry-transaction.model'
import { DirectTransfer } from '../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import { FXSpot } from '../model/single-transaction/fx-spot-trade/fx-spot.model'
import { SecurityTransfer } from '../model/single-transaction/security-transfer/security-transfer.model'
import { Trade } from '../model/single-transaction/trade/trade-transaction.model'
import { Transaction } from '../model/single-transaction/transaction'
import { WireTransfer } from '../model/single-transaction/wire-transfer/wire-transfer.model'
import { toISOFormatFromAnyValidDateString } from './common-utils'
import { getCurrencyFromId } from './currency-utils'

/**
 * map Bulk External transaction to External Transaction
 */
export function mapBulkExternalTransaction(
  bulkExtTransaction: ExternalUnmatchedTransaction[],
  status: Map<number, ExternalTransactionStatus | undefined>,
  currencyList?: Currency[],
): ExternalTransaction[] {
  return bulkExtTransaction.map(extTransaction => {
    return mapBulkExternalTransactionToExternalTransaction(
      extTransaction,
      status,
      currencyList,
    )
  })
}
/**
 * Mapping BulkExternalTransaction To external transaction
 */
export function mapBulkExternalTransactionToExternalTransaction(
  bulkExtTransaction: ExternalUnmatchedTransaction,
  bulkStatus: Map<number, ExternalTransactionStatus | undefined>,
  currencyList?: Currency[],
): ExternalTransaction {
  return {
    prometheusJson: { aggInfo: bulkExtTransaction.aggInfoJson },
    availableActions: [],
    extTranId: bulkExtTransaction.externalTransactionId,
    reconDate: '',
    accountId: bulkExtTransaction.accountId,
    rowId: '',
    secIdentifier: bulkExtTransaction.assetId,
    units: bulkExtTransaction.units,
    amount: bulkExtTransaction.amount,
    settleDate: bulkExtTransaction.settleDate,
    postDate: bulkExtTransaction.postDate,
    tradeDate: bulkExtTransaction.entryDate,
    tranTypeCode: bulkExtTransaction.tranType,
    counterPartyName: bulkExtTransaction.counterParty,
    clearwaterCounterParty: bulkExtTransaction.clearwaterCounterParty,
    currencyCode: currencyList
      ? getCurrencyFromId(bulkExtTransaction.currencyId, currencyList)?.code
      : '',
    executionId: bulkExtTransaction.executionId,
    status: bulkStatus.get(bulkExtTransaction.externalTransactionId),
    dataSourceType:
      bulkExtTransaction.dataSourceTypeId == 1
        ? ExternalDataSourceType.CUSTODY
        : ExternalDataSourceType.TRADING,
    fromUnits: bulkExtTransaction.fromUnits,
    fromAlternateId: bulkExtTransaction.fromAlternateId,
    securityId: bulkExtTransaction.securityId,
    price: bulkExtTransaction.price,
    factor: bulkExtTransaction.factor,
    accrued: bulkExtTransaction.accruedAmount,
    commission: bulkExtTransaction.commission,
    originationFee: bulkExtTransaction.originationFee,
    prepaidInterest: bulkExtTransaction.prepaidInterest,
    interestDue: bulkExtTransaction.interestDue,
    interestBearingInterestReserve:
      bulkExtTransaction.interestBearingInterestReserve,
    interestBearingPrincipalReserve:
      bulkExtTransaction.interestBearingPrincipalReserve,
    nonInterestBearingInterestReserve:
      bulkExtTransaction.nonInterestBearingInterestReserve,
    nonInterestBearingPrincipalReserve:
      bulkExtTransaction.nonInterestBearingPrincipalReserve,
    balanceToAdjust: bulkExtTransaction.balanceToAdjust,
    loanFeeType: bulkExtTransaction.loanFeeType,
    fromSecurityId: bulkExtTransaction.fromSecurityId,
    collateralBroker: bulkExtTransaction.aggInfoJson?.collateralBroker ?? '',
    isCapitalize: bulkExtTransaction.isCapitalize,
    transactionTypeLabel: bulkExtTransaction.transactionTypeLabel,
  }
}
/**
 * Mapping Trade Data to BulkTransaction Data
 */
export function mapTradeToBulkTransaction(cwTransactions: {
  [s: string]: Transaction
}): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapTradeToBulkTradeTransaction(value as Trade),
  )
}
/**
 * Mapping Trade Data to BulkTransaction Data
 */
export function mapCashEntryToBulkTransaction(
  cwTransactions: { [s: string]: Transaction },
  securityMasterData: Map<string, SecurityMasterData>,
  currencyList?: Currency[],
): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapCashEntryTransactionToBulkCashEntryTransaction(
      value as CashEntryTransaction,
      securityMasterData,
      currencyList,
    ),
  )
}
/**
 * Mapping FxSpot to BUlk Transaction
 */
export function mapFxForwardToBulkTransaction(
  cwTransactions: { [s: string]: Transaction },
  securityMasterData: Map<string, SecurityMasterData>,
): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapFxForwardToBulkFxForwardTransaction(value as FXSpot, securityMasterData),
  )
}
/**
 * Mapping Fx Trade to Bulk Transaction
 */
export function mapFxTradeToBulkTransaction(cwTransactions: {
  [s: string]: Transaction
}): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapFxSpotToBulkFxSpotTransaction(value as FXSpot),
  )
}
/**
 * Mapping Wire to Bulk Transaction
 */
export function mapWireToBulkTransaction(cwTransactions: {
  [s: string]: Transaction
}): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapWireToBulkWireTransaction(value as WireTransfer),
  )
}
/**
 * Mapping Direct Transfer to Bulk Transaction
 */
export function mapDirectTransferToBulkTransaction(
  cwTransactions: {
    [s: string]: Transaction
  },
  securityMasterData: Map<string, SecurityMasterData>,
): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapDirectTransferToBulkDirectTransferTransaction(
      value as DirectTransfer,
      securityMasterData,
    ),
  )
}

/**
 * Mapping initial Load to Bulk Transaction
 */
export function mapLoadToBulkTransaction(cwTransactions: {
  [s: string]: Transaction
}): BulkTransaction[] | undefined {
  return Object.values(cwTransactions).map(value =>
    mapLoadToBulkLoadTransaction(value as SecurityTransfer),
  )
}
/**
 *This Function is written to map ClearwaterTransaction to BulkCashEntryTransaction
 */
export function mapCashEntryTransactionToBulkCashEntryTransaction(
  cwTransaction: CashEntryTransaction,
  securityMasterData: Map<string, SecurityMasterData>,
  currencyList?: Currency[],
): BulkCashEntryTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    amount: cwTransaction.amount ? cwTransaction.amount : 0,
    active: cwTransaction.active,
    tranType: cwTransaction.tranType ?? 'BUY',
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    clearedDate: cwTransaction.clearedDate,
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    cusip: cwTransaction.securityId
      ? securityMasterData.get(cwTransaction.securityId.toString())?.cusip
      : '',
    currencyCode:
      cwTransaction.securityId && currencyList
        ? getCurrencyFromId(
            securityMasterData.get(cwTransaction.securityId.toString())
              ?.currencyId,
            currencyList,
          )?.code
        : '',
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    typeLabelId: Number(cwTransaction.typeLabelId),
    dataMap: cwTransaction.dataMap,
    isCapitalize:
      cwTransaction.dataMap?.isCapitalize != null
        ? 'true' === cwTransaction.dataMap?.isCapitalize
        : undefined,
  }
}
/**
 *  map clearwater transaction to Bulk Trade Transaction
 */
export function mapTradeToBulkTradeTransaction(
  cwTransaction: Trade,
): BulkTradeEntryTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    amount: cwTransaction.amount ? cwTransaction.amount : 0,
    commission: cwTransaction.commission,
    originationFee: cwTransaction.dataMap?.originationFee,
    prepaidInterest: cwTransaction.dataMap?.prepaidInterest,
    interestDue: cwTransaction.dataMap?.interestDue,
    yield: cwTransaction.yield ?? 0,
    counterPartyId: cwTransaction.counterPartyId,
    counterParty: cwTransaction.counterParty,
    clearedDate: cwTransaction.clearedDate,
    price: cwTransaction.price,
    units: cwTransaction.units,
    accrued: cwTransaction.accrued,
    factor: cwTransaction.factor,
    inventoryType: cwTransaction.inventoryType,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    amortOverrideType:
      cwTransaction.amortOverrideType ?? 'OBEY_ACCOUNT_SECURITY',
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    typeLabelId: cwTransaction.typeLabelId,
    dataMap: cwTransaction.dataMap,
  }
}
/**
 * map  cwTransaction into Bulk wire transaction transaction
 */
export function mapWireToBulkWireTransaction(
  cwTransaction: WireTransfer,
): BulkWireTransferTransaction {
  return {
    units: cwTransaction.units,
    active: true,
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    sourceAccountId: cwTransaction.accountId,
    destAccountId: cwTransaction.destAccountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    sourcePostDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    destPostDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    sourceClearedDate: toISOFormatFromAnyValidDateString(
      cwTransaction.sourceClearedDate ?? '',
    ),
    destClearedDate: toISOFormatFromAnyValidDateString(
      cwTransaction.destClearedDate ?? '',
    ),
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    typeLabelId: cwTransaction.typeLabelId,
    dataMap: cwTransaction.dataMap,
  }
}

/**
 * map  cwTransaction into Bulk wire transaction transaction
 */
export function mapLoadToBulkLoadTransaction(
  cwTransaction: SecurityTransfer,
): BulkLoadTransaction {
  return {
    units: cwTransaction.units,
    active: true,
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    clearedDate: cwTransaction.clearedDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.clearedDate)
      : undefined,
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    // PVAL-26104: carry the amount the transaction ALREADY has. Without it the
    // Bulk Load dialog held no record of the cash amount it was editing, so a
    // save whose derive produced no amount omitted the field entirely and the
    // Edit Transaction write path replaced the persisted value with zero — and
    // re-saving could not recover it (the reported "saving again does not
    // restore it"). prepareBulkTransactionData falls back to this value.
    amount: cwTransaction.amount,
    transferPrice: cwTransaction.transferPrice,
    clientLotId: cwTransaction.clientLotId,
    inventoryType: cwTransaction.inventoryType,
    dataMap: cwTransaction.dataMap,
    factor: cwTransaction.factor,
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    counterParty: cwTransaction.counterParty,
    traderId: cwTransaction.traderId,
    transactionBasis: cwTransaction.transactionBasis,
    basisId: cwTransaction.basisId,
  }
}

/**
 * map clearwater transaction to Bulk Fx Forward transaction
 */
export function mapFxForwardToBulkFxForwardTransaction(
  cwTransaction: FXSpot,
  securityMasterData: Map<string, SecurityMasterData>,
): BulkFXForwardTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    fromSecurityId: Number(cwTransaction.fromSecurityId),
    toSecurityId: Number(cwTransaction.toSecurityId),
    fromUnits: -1 * Math.abs(Number(cwTransaction.fromUnits)),
    toUnits: Math.abs(Number(cwTransaction.toUnits)),
    counterPartyId: cwTransaction.counterPartyId,
    counterParty: cwTransaction.counterParty,
    sellToBuyRate: Number(cwTransaction.sellToBuyRate),
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    amortOverrideType:
      cwTransaction.amortOverrideType ?? 'OBEY_ACCOUNT_SECURITY',
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    toSecurityCusip:
      securityMasterData.get(cwTransaction.securityId?.toString() ?? '0')
        ?.cusip ?? '',
    fromSecurityCusip:
      securityMasterData.get(
        cwTransaction.fromSecurityId
          ? cwTransaction.fromSecurityId.toString()
          : '',
      )?.cusip ?? '',
    typeLabelId: cwTransaction.typeLabelId,
    dataMap: cwTransaction.dataMap,
    clearedDate: cwTransaction.clearedDate,
  }
}
/**
 * mapping clearwaterTransaction to BulkFXSpotTransaction
 */
export function mapFxSpotToBulkFxSpotTransaction(
  cwTransaction: FXSpot,
): BulkFXSpotTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    fromSecurityId: Number(cwTransaction.fromSecurityId),
    toSecurityId: Number(cwTransaction.toSecurityId),
    fromUnits: Math.abs(Number(cwTransaction.fromUnits)),
    toUnits: Math.abs(Number(cwTransaction.toUnits)),
    counterPartyId: cwTransaction.counterPartyId,
    counterParty: cwTransaction.counterParty,
    sellToBuyRate: Number(cwTransaction.sellToBuyRate),
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    amortOverrideType:
      cwTransaction.amortOverrideType ?? 'OBEY_ACCOUNT_SECURITY',
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    typeLabelId: cwTransaction.typeLabelId,
    dataMap: cwTransaction.dataMap,
  }
}

/**
 * map  cwTransaction into Bulk Direct Transfer transaction
 */
export function mapDirectTransferToBulkDirectTransferTransaction(
  cwTransaction: DirectTransfer,
  securityMasterData: Map<string, SecurityMasterData>,
): BulkDirectTransferTransaction {
  return {
    units: cwTransaction.units,
    active: true,
    tranType: cwTransaction.tranType,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    destAccountId: cwTransaction.destAccountId ?? cwTransaction.xferAccountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    clearedDate: cwTransaction.clearedDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.clearedDate)
      : undefined,
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.legacyOrigTranId,
    legacyTranId: cwTransaction.legacyTranId,
    transferPrice: cwTransaction.transferPrice,
    inventoryType: cwTransaction.inventoryType,
    dataMap: cwTransaction.dataMap,
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    counterParty: cwTransaction.counterParty,
    traderId: cwTransaction.traderId,
    cusip: cwTransaction.securityId
      ? securityMasterData.get(cwTransaction.securityId.toString())?.cusip
      : '',
    transactionBasis: cwTransaction.transactionBasis,
    basisId: cwTransaction.basisId,
  }
}
